import { Component, OnInit, Input, ViewChild, AfterViewInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Location } from '@angular/common';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';
import { AlertService, DialogType, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from "../../../services/app-translation.service";
import { Utilities } from "../../../services/utilities";
import { ReportRequirementViewModel } from '../../model/reportRequirementViewModel';
import { ReportService } from '../../api/report.service';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormArray } from '@angular/forms';
import { AuthService } from '../../../services/auth.service';
import { Permission } from '../../../models/permission.model';

@Component({
    selector: 'app-report-requirement-list',
    templateUrl: './report-requirement-list.component.html',
    styleUrls: ['./report-requirement-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportRequirementListComponent implements OnInit {
    @Input() id: number;
    @Input() hasPermissionToChange$: Observable<boolean>;

    requirementForm: UntypedFormGroup;

    loadingIndicator: boolean;
    isSaving: boolean = false;

    public requirementCount: number = 0;
    public fulfilledRequirementCount : number = 0;

    constructor(
        private alertService: AlertService,
        private translationService: AppTranslationService,
        private authService: AuthService,
        private reportService: ReportService,
        private location: Location,
        private formBuilder: UntypedFormBuilder,
        private snackBar : MatSnackBar) {

    }

    ngOnInit() {
        this.buildForm();
        this.loadData();
    }

    buildForm() {
        this.requirementForm = this.formBuilder.group({
            requirements: this.formBuilder.array([])
        });
    }

    resetForm(requirements: ReportRequirementViewModel[]) {

        this.requirementForm.reset();
        this.requirementCount = requirements.length;
        this.fulfilledRequirementCount = requirements.filter(t => t.fulfilled).length;

        let requirementFormGroups = requirements.map(requirement => this.formBuilder.group(requirement));
        let requirementFormArray = this.formBuilder.array(requirementFormGroups);
        this.requirementForm.setControl('requirements', requirementFormArray);
    }

    get requirementsArray(): UntypedFormArray {
        return this.requirementForm.get('requirements') as UntypedFormArray;
    }

    // Signal to the parent component if this component is dirty
    public isDirty(): boolean {
        return this.requirementForm.dirty;
    }

    public loadData() {
        this.alertService.startLoadingMessage();
        this.loadingIndicator = true;

        this.reportService.getRequirementsByReportId(this.id).subscribe(
            reqs => this.onDataLoadSuccessful(reqs),
            error => this.onDataLoadFailed(error)
        );
    }

    private onDataLoadSuccessful(requirements: ReportRequirementViewModel[]) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;
        this.resetForm(requirements);
    }

    private onDataLoadFailed(error: any) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;

        this.alertService.showStickyMessage(this.translationService.getTranslation("Error"),
            this.translationService.getTranslation("FetchErrorMessage") +
            "\r\n" +
            Utilities.getHttpResponseMessage(error),
            MessageSeverity.error,
            error);
    }

    public fulFilledChanged(item: MatCheckbox) {
        if (item.checked) {
            this.fulfilledRequirementCount++;
        } else {
            this.fulfilledRequirementCount--;
        }
    }

    public save() {
        if (!this.requirementForm.valid) {
            this.alertService.showValidationError();
            return;
        }

        this.isSaving = true;
        this.alertService.startLoadingMessage(this.translationService.getTranslation("globalTexts.SavingMessage"));

        let requirementContainer = this.requirementForm.value;
        let requirements: ReportRequirementViewModel[] = requirementContainer.requirements;

        this.reportService.updateReportRequirements(requirements).subscribe(
            t => this.saveCompleted(t),
            error => this.saveFailed(error));

    }

    private saveCompleted(reportRequirements: ReportRequirementViewModel[]) {
        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showMessage(this.translationService.getTranslation("commands.Save"), "Krav " + this.translationService.getTranslation("globalTexts.saved"), MessageSeverity.success);

        this.resetForm(reportRequirements);
    }

    private saveFailed(error: any) {
        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage(this.translationService.getTranslation("globalTexts.CouldNotSave"), this.translationService.getTranslation("globalTexts.SaveErrorMessage"), MessageSeverity.error, error);
        this.alertService.showStickyMessage(error, null, MessageSeverity.error);
    }


    public cancel() {
        this.location.back();
    }
}
